import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { CircularProgress } from "@material-ui/core";
import DriveModeScreen from "../../components/drive-mode-screen/drive-mode-screen";
import { driveModeAvailable } from "../../components/drive-mode-screen/drive-mode-flags";
import { api } from "../../services/apiService";

// Setup guard rails (Item 5). ~20s before we surface "still working" actions so a
// slow createQuiz/getQuiz/metadata never looks like an indefinite hang. apiService
// RESOLVES error objects (errStatus/status + errMsg) instead of throwing, so each
// call is checked explicitly below rather than relying on a try/catch alone.
const SETUP_TIMEOUT_MS = 20000;
const isDevEnv = process.env.NODE_ENV === "development";

// Per-call latency attribution, logged in dev only (console.time-style) so on the
// next repro an API-latency hang is distinguishable from a UX gap in the console.
const timeSetupCall = async (label, run) => {
  const startedAt = Date.now();
  try {
    return await run();
  } finally {
    if (isDevEnv) {
      // eslint-disable-next-line no-console
      console.log(`[AudioMode setup] ${label}: ${Date.now() - startedAt}ms`);
    }
  }
};

// An apiService result is an error when it is falsy, a raw Error, or carries the
// resolved error shape. apiService wraps EVERY non-200 as a {status,errMsg}
// envelope, so we treat errStatus, a present envelope errMsg, or a numeric HTTP
// status >= 400 as failure. The errMsg check hardens the numeric-status test so a
// synthetic error envelope with a non->=400 status (e.g. {status:204,errMsg}) is
// still routed to recovery instead of degrading as empty metadata (finding-3
// envelope caveat, rr-d6665466784d).
const isApiErrorResult = (result) =>
  !result ||
  result instanceof Error ||
  result.errStatus !== undefined ||
  result.errMsg !== undefined ||
  (typeof result.status === "number" && result.status >= 400);

// Dark, self-contained styling (the page has no stylesheet; the screen owns the
// SCSS). Matches the Audio Mode screen palette so setup never flashes light.
const SETUP_SURFACE = {
  minHeight: "100vh",
  boxSizing: "border-box",
  padding: "24px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  textAlign: "center",
  color: "#fff",
  background: "#101827",
};
const SETUP_PRIMARY_BTN = {
  minHeight: "52px",
  minWidth: "220px",
  padding: "12px 20px",
  border: 0,
  borderRadius: "10px",
  fontWeight: 700,
  background: "#8dc5ff",
  color: "#0b1220",
};
const SETUP_SECONDARY_BTN = {
  minHeight: "52px",
  minWidth: "220px",
  padding: "12px 20px",
  borderRadius: "10px",
  fontWeight: 700,
  background: "#172943",
  color: "#cfe2ff",
  border: "1px solid #33507a",
};

export const buildSessionPayload = ({ quizId, userId, questions, picks, submittedAt = moment() }) => {
  const questionHistories = questions
    .filter((question) => picks.has(question.questionId))
    .map((question) => ({
      questionId: question.questionId,
      answerGiven: picks.get(question.questionId),
      marked: false,
      timeSpent: 0,
      answersAttempted: [],
      questionNo: question.questionNo,
    }));
  return {
    questionHistories,
    quizHistory: {
      quizId,
      userId,
      questionAttemptCount: questionHistories.length,
      quizSubmitDate: `${submittedAt.format("YYYY-MM-DDTHH:mm:ss.sss")}Z`,
      quizDuration: 0,
    },
  };
};

export const assertSubmitSuccess = (result) => {
  if (result?.errStatus || result?.errMsg) {
    throw new Error(result.errMsg || `Quiz submission failed (${result.errStatus})`);
  }
  return result;
};

export const createSessionSubmitter = ({ session, submitQuiz, onState, retryMs = 5000, wait = setTimeout }) => {
  const picks = new Map();
  let finalPromise = null;
  const recordPick = (questionId, optionKey) => {
    if (!finalPromise) picks.set(questionId, optionKey);
    return Promise.resolve();
  };
  const finalize = () => {
    if (finalPromise) return finalPromise;
    if (picks.size === 0) {
      finalPromise = Promise.resolve();
      return finalPromise;
    }
    const payload = buildSessionPayload({ ...session, picks });
    const attempt = () => Promise.resolve().then(() => submitQuiz(payload)).then(assertSubmitSuccess);
    onState?.({ status: "syncing" });
    finalPromise = attempt()
      .catch((firstError) => {
        onState?.({ status: "sync-pending", error: firstError });
        return new Promise((resolve) => wait(resolve, retryMs)).then(attempt);
      })
      .then((result) => {
        onState?.({ status: "synced", result });
        return result;
      })
      .catch((error) => {
        onState?.({ status: "failed", error });
        return undefined;
      });
    return finalPromise;
  };
  return { recordPick, finalize, getPicks: () => new Map(picks) };
};

const toQueue = (quiz, metadata) => {
  const byId = new Map((metadata || []).map((item) => [item.id, item]));
  return (quiz?.questions || []).map((item, index) => {
    const questionId = item.questionId || item.id;
    const detail = byId.get(questionId) || item;
    // Real getQuestionMetadata carries options as detail.option.optionList (an
    // array of plain strings); the {key,text} form is the pre-existing fixture
    // shape. Try the explicit shape first, then fall back to optionList.
    const rawOptions = detail.options || detail.answerOptions || detail.answers
      || (detail.option?.optionList || []).map((text) => ({ text }));
    return {
      questionId,
      questionNo: item.questionNo || index + 1,
      // Read-along text for the glance-while-running surface. Field names verified
      // against the production metadata consumers: exam-question-container reads
      // detail.stem; exam-review-drawer reads briefExplanation/additionalExplanation
      // (student-facing) and admin-only explanation. Render whichever is present.
      stem: detail.stem || detail.questionText || detail.question || "",
      explanation: detail.briefExplanation || detail.additionalExplanation || detail.explanation || "",
      // S2/D7 — the REAL correct-answer STRING threaded from metadata
      // (option.correctAnswer), exactly like exam-question-container which compares
      // correctAnswer to the option text — NEVER the synthesized A/B/C display
      // letter. The screen marks the pick by comparing the option value to this.
      correctAnswer: detail.option?.correctAnswer || detail.correctAnswer || "",
      options: rawOptions.map((option, optionIndex) => ({
        ...option,
        key: option.key || option.optionKey || String.fromCodePoint(65 + optionIndex),
        // S2/D2 — per-question OPAQUE row identity, minted ONCE here (ordinal
        // r0…rN). Distinct from BOTH the display letter (`key`) and the answer
        // payload (option text); collision-free by construction — no case folding,
        // no content derivation. React keys, DOM test-ids and chosen-tracking use
        // ONLY this id, so `a`/`A` and the 33rd generated letter never alias.
        rowId: `r${optionIndex}`,
      })),
    };
  });
};

export default function DriveModePage() {
  const history = useHistory();
  const location = useLocation();
  const enabled = driveModeAvailable();
  const [session, setSession] = useState(null);
  const [setupStep, setSetupStep] = useState("creating"); // 'creating' | 'loading'
  const [setupError, setSetupError] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0); // Try-again re-runs the setup pipeline
  const [keepWaitingNonce, setKeepWaitingNonce] = useState(0); // Keep-waiting re-arms the timeout
  const [sync, setSync] = useState({ status: "active" });
  const mountedRef = useRef(true);
  const quizParams = location.state?.quizParams;

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Creation/loading pipeline. apiService RESOLVES error objects instead of
  // throwing, so createQuiz/getQuiz are checked explicitly (created?.id / status)
  // and each call is timed for dev latency attribution.
  useEffect(() => {
    if (!enabled || !quizParams) return undefined;
    let cancelled = false;
    setSetupError(false);
    setTimedOut(false);
    setSetupStep("creating");

    (async () => {
      const created = await timeSetupCall("createQuiz", () => api.createQuiz(quizParams));
      if (cancelled) return;
      if (isApiErrorResult(created) || !created.id) { setSetupError(true); return; }

      setSetupStep("loading");
      const quiz = await timeSetupCall("getQuiz", () => api.getQuiz(created.id));
      if (cancelled) return;
      if (isApiErrorResult(quiz)) { setSetupError(true); return; }

      const ids = (quiz.questions || []).map((item) => item.questionId || item.id);
      const metaResult = ids.length
        ? await timeSetupCall("getQuestionMetadata", () => api.getQuestionMetadata({ question_list: ids }))
        : [];
      if (cancelled) return;
      // apiService RESOLVES its normal failures (a {status,errMsg} object for a
      // non-200, an Error for a network drop) rather than throwing, so a resolved
      // metadata error must route to the human error + Try-again recovery exactly
      // like createQuiz/getQuiz — never be coerced to [] and rendered as an empty,
      // ID-only player (finding 3).
      if (isApiErrorResult(metaResult)) { setSetupError(true); return; }
      // Read-along enrichment: a genuinely-empty (non-error) result still degrades
      // to empty rather than failing the whole session.
      const metadata = Array.isArray(metaResult) ? metaResult : [];
      setSession({ quizId: created.id, userId: quiz.userId || quizParams.userId, questions: toQueue(quiz, metadata) });
    })().catch(() => { if (!cancelled) setSetupError(true); });

    return () => { cancelled = true; };
  }, [enabled, quizParams, attempt]);

  // Setup timeout: while the session is still building (not ready, no error), arm
  // a ~20s timer that surfaces the "still working" actions. Keep-waiting re-arms.
  useEffect(() => {
    const loading = enabled && quizParams && !session && !setupError;
    if (!loading) return undefined;
    setTimedOut(false);
    const id = setTimeout(() => { if (mountedRef.current) setTimedOut(true); }, SETUP_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [enabled, quizParams, session, setupError, keepWaitingNonce]);

  const submitter = useMemo(() => session && createSessionSubmitter({
    session,
    submitQuiz: api.submitQuiz,
    onState: (next) => { if (mountedRef.current) setSync(next); },
  }), [session]);

  const backToBuilder = useCallback(() => history.push("/create-quiz"), [history]);
  const retrySetup = useCallback(() => { setSetupError(false); setSession(null); setAttempt((n) => n + 1); }, []);
  const keepWaiting = useCallback(() => setKeepWaitingNonce((n) => n + 1), []);

  if (!enabled) return null;
  // User-facing copy says "Audio Mode"; the route ("/drive"), flags and export
  // stay "drive" (deliberate internal/external naming split).
  // Direct visit (bookmark, pasted link) arrives with no launch state — a real
  // screen with a route back, not a bare line: the first dev tutor to test hit
  // exactly this as a blank-feeling dead end (2026-08-08 pilot feedback).
  if (!quizParams) {
    return (
      <div data-testid="drive-empty-state" style={SETUP_SURFACE}>
        <p style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>Audio Mode</p>
        <p style={{ margin: 0, color: "#cfe2ff" }}>
          Pick your questions in the quiz builder first — Audio Mode starts from there.
        </p>
        <button type="button" style={SETUP_PRIMARY_BTN} onClick={backToBuilder}>
          Go to the quiz builder
        </button>
      </div>
    );
  }

  // Dark error recovery — a human message only, never the raw error text.
  if (setupError) {
    return (
      <div role="alert" data-testid="drive-setup-error" style={SETUP_SURFACE}>
        <p style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>We couldn't start Audio Mode.</p>
        <p style={{ margin: 0, color: "#cfe2ff" }}>Something went wrong preparing your session. Please try again.</p>
        <button type="button" style={SETUP_PRIMARY_BTN} onClick={retrySetup}>Try again</button>
        <button type="button" style={SETUP_SECONDARY_BTN} onClick={backToBuilder}>Back to quiz builder</button>
      </div>
    );
  }

  // Dark progress — which step + spinner, and after the timeout the keep-waiting
  // / back-to-builder actions.
  if (!session || !submitter) {
    const stepLabel = setupStep === "loading" ? "Loading your questions…" : "Creating your quiz…";
    return (
      <div role="status" aria-live="polite" data-testid="drive-setup-progress" style={SETUP_SURFACE}>
        <CircularProgress size={44} style={{ color: "#8dc5ff" }} />
        <p data-testid="drive-setup-step" style={{ fontSize: "1.2rem", fontWeight: 600, margin: 0 }}>{stepLabel}</p>
        {timedOut && (
          <div data-testid="drive-setup-timeout" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
            <p style={{ margin: 0, color: "#cfe2ff" }}>Still working on your Audio Mode session…</p>
            <button type="button" style={SETUP_PRIMARY_BTN} onClick={keepWaiting}>Keep waiting</button>
            <button type="button" style={SETUP_SECONDARY_BTN} onClick={backToBuilder}>Back to quiz builder</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <DriveModeScreen
      questions={session.questions}
      submitAnswer={submitter.recordPick}
      onSessionEnd={submitter.finalize}
      sync={sync}
      onExit={() => { submitter.finalize(); history.push("/create-quiz"); }}
    />
  );
}
